import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PlanCheckPipe } from '@app/pipes/plan-check.pipe';
import { TrackingService } from '@app/services/tracking.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonThemePalette } from '@sb/ui/button';
import { DIALOG_DATA, DialogActivationComponent, DialogRef } from '@sb/ui/dialog';
import { InputModule } from '@sb/ui/input';
import { RadioComponent } from '@sb/ui/radio';
import { RadioGroupComponent } from '@sb/ui/radio-group';
import { finalize, from, switchMap } from 'rxjs';

import { OnboardingDialogResult } from '../../../+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { KnowledgeBaseArticleLinkModule } from '../../../pipes/knowledge-base-article-link.module';
import { OnboardingUtils } from '../../onboarding.utils';
import { ChapterRadioButtonData } from '../chapter.model';
import { OnboardingChapterModule } from '../components/chapter.module';
import {
  AbsenceManagementDialogData,
  AbsenceManagementDialogStep,
  DefaultScheduledShiftAction,
} from './absence-management.model';
import { OnboardingAbsenceManagementService } from './absence-management.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'absence-management-dialog',
  imports: [
    DialogActivationComponent,
    FormsModule,
    NgStyle,
    TranslatePipe,
    OnboardingChapterModule,
    InputModule,
    KnowledgeBaseArticleLinkModule,
    RadioGroupComponent,
    RadioComponent,
    ReactiveFormsModule,
  ],
  providers: [PlanCheckPipe],
  templateUrl: './absence-management-dialog.component.html',
})
export class AbsenceManagementDialogComponent {
  data = inject<AbsenceManagementDialogData>(DIALOG_DATA);
  private trackingService = inject(TrackingService);
  private dialogRef = inject<DialogRef<OnboardingDialogResult>>(DialogRef);
  private translate = inject(TranslateService);
  private service = inject(OnboardingAbsenceManagementService);
  private onboardingUtils = inject(OnboardingUtils);

  ButtonThemePalette = ButtonThemePalette;

  AbsenceManagementDialogStep = AbsenceManagementDialogStep;
  currentStep = AbsenceManagementDialogStep.TEASER;
  steps = 2;

  nextButtonLoading = false;
  private eventTracked = false;

  defaultRosterAction = new FormControl(null, Validators.required);
  rosterActionOptions: ChapterRadioButtonData[] = [
    {
      value: DefaultScheduledShiftAction.REMOVE_FROM_SCHEDULE,
      label: this.translate.instant('Remove from schedule'),
      description: this.translate.instant('Hide the shift only when the absence is approved.'),
    },
    {
      value: DefaultScheduledShiftAction.LEAVE_IN_SCHEDULE,
      label: this.translate.instant('Leave in schedule'),
      description: this.translate.instant('Keep the shift as scheduled, even during the absence.'),
    },
    {
      // Needs at least basic
      value: DefaultScheduledShiftAction.MOVE_TO_OPEN_SHIFT,
      label: this.translate.instant('Move to open shifts'),
      description: this.translate.instant('Make the shift available for others to pick up.'),
    },
  ];

  constructor() {
    const data = this.data;

    if (data.step) {
      this.currentStep = data.step;
    }
  }

  get chapterCTAs() {
    return this.onboardingUtils.chapterCTAs();
  }

  goBack() {
    this.currentStep = this.currentStep - 1;
  }

  savePreferences() {
    this.nextButtonLoading = true;

    void this.service
      .savePreferences({
        defaultScheduledShiftAction: this.defaultRosterAction.value,
      })
      .pipe(
        switchMap(() => from(this.service.completeChapter(this.data.eventData))),
        finalize(() => (this.nextButtonLoading = false)),
      )
      .subscribe(() => {
        this.currentStep = AbsenceManagementDialogStep.SUMMARY;
      });
  }

  close(result: OnboardingDialogResult) {
    if (this.currentStep === AbsenceManagementDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Onboarding Absence Management Started', this.data.eventData);
      this.eventTracked = true;
    }

    this.dialogRef.close(result);
  }
}
